/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { ReactNode, useEffect, useState } from 'react';
import { Button } from 'components/_new/Button';
import GoogleIcon from 'assets/icons/google-logo.svg';
import OfficeIcon from 'assets/icons/office-logo.svg';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { linkedAccountApi } from 'api';
import { useAuth } from 'contexts/authContext';
import { DialogConfirm } from 'components/_new/Dialog';
import { IdentityProvider, LinkedAccount, LinkedAccountStatus } from 'types/LinkedAccount';
import { Icon } from 'components/_new/Icon';
import { classNames } from 'primereact/utils';
import { dateFormat } from 'utils/dateFormat';
import { Link } from 'components/_new/Link';
import { getLinkedAccountReauthMessageKey } from 'utils/linkedAccountErrorMapping';
import { useAuthIntegration } from './useAuthIntegration';

type LinkedAccountListPropos = {
	initialData?: LinkedAccount[];
	manageMode?: boolean;
	onRemove?: () => void;
	onLabelClick?: (
		linkedAccount: Pick<
			LinkedAccount,
			| 'id'
			| 'status'
			| 'provider'
			| 'email'
			| 'createdAt'
			| 'info'
			| 'primaryAccount'
			| 'reauthRequired'
			| 'reauthRequiredReason'
		>
	) => void;
	renderBeforeItem?: (
		linkedAccount: Pick<
			LinkedAccount,
			| 'id'
			| 'status'
			| 'provider'
			| 'email'
			| 'createdAt'
			| 'info'
			| 'primaryAccount'
			| 'reauthRequired'
			| 'reauthRequiredReason'
		>
	) => ReactNode;
	displayAdditionalInformation?: boolean;
	integrateParams?: {
		programId?: number;
		membershipId?: number;
		sessionId?: number;
	};
	enabled?: boolean;
};

export const LinkedAccountList = (props: LinkedAccountListPropos) => {
	const {
		initialData,
		manageMode = false,
		onRemove,
		onLabelClick,
		renderBeforeItem,
		displayAdditionalInformation = false,
		integrateParams,
		enabled,
	} = props;

	const { t } = useTranslation();
	const { currentUser } = useAuth();

	const { addGoogleAccountMutate, addGoogleAccountLoading, addMicrosoftAccountMutate, addMicrosoftAccountLoading } =
		useAuthIntegration(integrateParams);

	const { mutate: reauthMicrosoftAccountMutate, isLoading: reauthMicrosoftAccountLoading } = useMutation(
		(id: number) => linkedAccountApi.reauthMicrosoftAccount(id, integrateParams),
		{
			onSuccess: (data) => {
				window.localStorage.setItem(`microsoft-auth-redirect`, window.location.pathname);
				setTimeout(() => {
					window.location.href = data.redirectUrl;
				}, 500);
			},
		}
	);

	const { mutate: reauthGoogleAccountMutate, isLoading: reauthGoogleAccountLoading } = useMutation(
		(id: number) => linkedAccountApi.reauthGoogleAccount(id, integrateParams),
		{
			onSuccess: (data) => {
				window.localStorage.setItem(`google-auth-redirect`, window.location.pathname);
				setTimeout(() => {
					window.location.href = data.redirectUrl;
				}, 500);
			},
		}
	);

	const { mutate: addMicrosoftAccountCalendarMutate, isLoading: addMicrosoftAccountCalendarLoading } = useMutation(
		(id: number) => linkedAccountApi.addMicrosoftAccountCalendar(id, integrateParams),
		{
			onSuccess: (data) => {
				window.localStorage.setItem(`microsoft-auth-redirect`, window.location.pathname);
				setTimeout(() => {
					window.location.href = data.redirectUrl;
				}, 500);
			},
		}
	);

	const { mutate: addGoogleAccountCalendarMutate, isLoading: addGoogleAccountCalendarLoading } = useMutation(
		(id: number) => linkedAccountApi.addGoogleAccountCalendar(id, integrateParams),
		{
			onSuccess: (data) => {
				window.localStorage.setItem(`google-auth-redirect`, window.location.pathname);
				setTimeout(() => {
					window.location.href = data.redirectUrl;
				}, 500);
			},
		}
	);

	const { data: linkedAccounts, refetch } = useQuery(
		['GET_LINKED_ACCOUNTS', { id: Number(currentUser?.id) }],
		() => linkedAccountApi.getLinkedAccounts(),
		{ enabled: enabled !== undefined ? enabled : true, initialData: initialData || [] }
	);

	useEffect(() => {
		refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialData]);

	const { mutateAsync: removeLinkedAccount, isLoading: removeLinkedAccountLoading } = useMutation(
		({ id }: { id: number }) => linkedAccountApi.removeLinkedAccount(id),
		{
			onSuccess: () => {
				refetch();
				onRemove?.();
			},
		}
	);

	const [selectedId, setSelectedId] = useState<number | null>(null);

	const handleRemoveConfirm = () => {
		if (selectedId) {
			removeLinkedAccount({ id: selectedId }).finally(() => {
				setSelectedId(null);
			});
		}
	};

	const handleRemoveReject = () => {
		setSelectedId(null);
	};

	return (
		<>
			{linkedAccounts.length > 0 && (
				<ul className="flex flex-column gap-1 p-0 w-full">
					{linkedAccounts.map(
						({
							id,
							status,
							provider,
							email,
							createdAt,
							info,
							primaryAccount,
							reauthRequired,
							reauthRequiredReason,
						}) => (
							<li key={id} className="flex flex-row gap-2 p-0 border-bottom-1 py-2">
								<div className="flex flex-row align-items-center gap-2">
									{renderBeforeItem
										? renderBeforeItem({
												id,
												status,
												provider,
												email,
												createdAt,
												info,
												primaryAccount,
												reauthRequired,
												reauthRequiredReason,
										  })
										: null}

									<span className="text-center" style={{ minWidth: '2rem' }}>
										{provider === IdentityProvider.GOOGLE && <img src={GoogleIcon} alt="icon" />}
										{provider === IdentityProvider.MICROSOFT && <img src={OfficeIcon} alt="icon" />}
									</span>
									{displayAdditionalInformation && status === LinkedAccountStatus.READY && (
										<Icon
											name="calendar-days"
											iconStyle="regular"
											className={classNames('text-xxl pb-1', {
												'text-primary': !reauthRequired && info?.calendarAccess,
												'text-muted': reauthRequired || !info?.calendarAccess,
											})}
											style={{ minWidth: '2rem' }}
										/>
									)}
									<div className="flex flex-column gap-1 gap-0">
										{status === LinkedAccountStatus.READY ? (
											<div
												onClick={
													onLabelClick
														? () => {
																onLabelClick({
																	id,
																	status,
																	provider,
																	email,
																	createdAt,
																	info,
																	primaryAccount,
																	reauthRequired,
																	reauthRequiredReason,
																});
														  }
														: undefined
												}
											>
												{email}
											</div>
										) : (
											<div className="flex flex-row gap-1">
												<Button
													label={t('linkedAccount.myAccount.actions.continue')}
													onClick={(event) => {
														switch (provider) {
															case IdentityProvider.GOOGLE:
																addGoogleAccountMutate();
																break;
															case IdentityProvider.MICROSOFT:
																addMicrosoftAccountMutate();
																break;
															default:
																event.preventDefault();
														}
													}}
													variantSize="sm"
													loading={
														provider === IdentityProvider.GOOGLE
															? addGoogleAccountLoading
															: addMicrosoftAccountLoading
													}
													disabled={removeLinkedAccountLoading}
												/>
												<Button
													label={t('linkedAccount.myAccount.actions.cancel')}
													onClick={() => {
														setSelectedId(id);
													}}
													variant="danger-text"
													variantSize="sm"
													loading={removeLinkedAccountLoading}
													disabled={addGoogleAccountLoading || addMicrosoftAccountLoading}
												/>
											</div>
										)}

										{displayAdditionalInformation && (
											<div className="text-muted text-sm">
												{dateFormat(createdAt, 'dayAndMonthPlusYear')}
											</div>
										)}

										{status === LinkedAccountStatus.READY &&
											(reauthRequired || !info?.calendarAccess) && (
												<div className="text-xs text-danger">
													<Icon name="info-circle" />
													<span className="mx-2">
														{reauthRequired
															? t(getLinkedAccountReauthMessageKey(reauthRequiredReason))
															: t('linkedAccount.myAccount.noCalendarAccess')}
													</span>
													<Link
														to="#"
														onClick={(event) => {
															switch (provider) {
																case IdentityProvider.GOOGLE:
																	if (reauthRequired) {
																		reauthGoogleAccountMutate(id);
																	} else {
																		addGoogleAccountCalendarMutate(id);
																	}
																	break;
																case IdentityProvider.MICROSOFT:
																	if (reauthRequired) {
																		reauthMicrosoftAccountMutate(id);
																	} else {
																		addMicrosoftAccountCalendarMutate(id);
																	}
																	break;
																default:
																	event.preventDefault();
																	break;
															}
														}}
														variant="standard"
														disabled={
															reauthGoogleAccountLoading ||
															reauthMicrosoftAccountLoading ||
															addGoogleAccountCalendarLoading ||
															addMicrosoftAccountCalendarLoading
														}
													>
														{reauthRequired
															? t('linkedAccount.myAccount.actions.reauth')
															: t('linkedAccount.myAccount.actions.allow')}
													</Link>
												</div>
											)}
									</div>
								</div>
								{manageMode && status === LinkedAccountStatus.READY && (
									<div className="flex flex-row gap-2 align-items-center ml-auto">
										<Button
											onClick={() => {
												setSelectedId(id);
											}}
											label={t('linkedAccount.myAccount.actions.unlink')}
											icon="link"
											variant="danger"
											variantSize="sm"
											loading={removeLinkedAccountLoading}
											disabled={status !== LinkedAccountStatus.READY || primaryAccount}
											tooltip={
												primaryAccount
													? t('linkedAccount.myAccount.canNotUnlinkPrimaryAccount')
													: undefined
											}
											tooltipPosition="left"
										/>
									</div>
								)}
							</li>
						)
					)}
				</ul>
			)}
			<DialogConfirm
				visible={selectedId !== null}
				onConfirm={handleRemoveConfirm}
				onReject={handleRemoveReject}
			/>
		</>
	);
};
